import { Task, TaskHistoryEntry, Note } from '../types';
import {
  mergeTaskFieldsWithBaseline,
  mergeTaskFieldsConflictFree,
} from '../hooks/project/sync/projectSyncWatermarks';

export { mergeTaskFieldsWithBaseline, mergeTaskFieldsConflictFree };

/**
 * Normalizes field values for reliable comparison (e.g. treats undefined, null, and empty string as equivalent for optional strings).
 */
export function normalizeValue(val: unknown): unknown {
  if (val === undefined || val === null) return null;
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val;
  if (Array.isArray(val)) return JSON.stringify(val);
  if (typeof val === 'object') return JSON.stringify(val);
  return val;
}

/**
 * Checks if two field values are meaningfully different.
 */
export function isFieldDifferent(valA: unknown, valB: unknown): boolean {
  const normA = normalizeValue(valA);
  const normB = normalizeValue(valB);
  
  // If one is empty string / null and the other is empty string / null, they are considered equal
  const isEmptyA = normA === null || normA === '';
  const isEmptyB = normB === null || normB === '';
  if (isEmptyA && isEmptyB) return false;

  return normA !== normB;
}

/**
 * Merges two Note collections (supports both string notes and Note[] arrays).
 */
export function mergeNotes(localNotes?: string | Note[], remoteNotes?: string | Note[], baselineNotes?: string | Note[]): string | Note[] | undefined {
  // Case 1: Both are arrays
  if (Array.isArray(localNotes) || Array.isArray(remoteNotes)) {
    const arrLocal: Note[] = Array.isArray(localNotes) ? localNotes : (localNotes ? [{ id: 'loc-1', text: localNotes, timestamp: Date.now() }] : []);
    const arrRemote: Note[] = Array.isArray(remoteNotes) ? remoteNotes : (remoteNotes ? [{ id: 'rem-1', text: remoteNotes, timestamp: Date.now() }] : []);
    
    const noteMap = new Map<string, Note>();
    // Collect all notes keyed by ID or text+timestamp
    [...arrRemote, ...arrLocal].forEach(n => {
      const key = n.id || `${n.text}_${n.timestamp}`;
      if (!noteMap.has(key)) {
        noteMap.set(key, n);
      }
    });
    return Array.from(noteMap.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }

  // Case 2: String notes
  const strLocal = typeof localNotes === 'string' ? localNotes.trim() : '';
  const strRemote = typeof remoteNotes === 'string' ? remoteNotes.trim() : '';
  const strBaseline = typeof baselineNotes === 'string' ? baselineNotes.trim() : '';

  if (strLocal === strRemote) return strLocal || undefined;
  if (!strLocal) return strRemote || undefined;
  if (!strRemote) return strLocal || undefined;

  // 3-way string check
  const localChanged = strLocal !== strBaseline;
  const remoteChanged = strRemote !== strBaseline;

  if (remoteChanged && !localChanged) return strRemote;
  if (localChanged && !remoteChanged) return strLocal;

  // Both changed to different texts: preserve local, append remote if distinct
  if (strLocal.includes(strRemote)) return strLocal;
  if (strRemote.includes(strLocal)) return strRemote;

  return `${strLocal}\n\n[Remote Ergänzung]:\n${strRemote}`;
}

/**
 * Merges checklist items by item ID with 3-way diffing.
 */
export function mergeChecklists(
  localList?: { id: string; text: string; completed: boolean }[],
  remoteList?: { id: string; text: string; completed: boolean }[],
  baselineList?: { id: string; text: string; completed: boolean }[]
): { id: string; text: string; completed: boolean }[] | undefined {
  if (!localList && !remoteList) return undefined;
  const loc = localList || [];
  const rem = remoteList || [];
  const base = baselineList || [];

  const baseMap = new Map(base.map(item => [item.id, item]));
  const locMap = new Map(loc.map(item => [item.id, item]));
  const remMap = new Map(rem.map(item => [item.id, item]));

  const mergedItemIds = new Set<string>([
    ...loc.map(i => i.id),
    ...rem.map(i => i.id)
  ]);

  const result: { id: string; text: string; completed: boolean }[] = [];

  mergedItemIds.forEach(id => {
    const l = locMap.get(id);
    const r = remMap.get(id);
    const b = baseMap.get(id);

    // Newly added on remote
    if (!l && r) {
      if (!b) result.push(r); // New remote item
      return; // Was deleted locally if b exists
    }

    // Newly added on local
    if (l && !r) {
      if (!b) result.push(l); // New local item
      return; // Was deleted remotely if b exists
    }

    // Present in both
    if (l && r) {
      if (!b) {
        // Exists in both without baseline: local preference, or completed if either completed
        result.push({
          id,
          text: l.text || r.text,
          completed: l.completed ?? r.completed
        });
        return;
      }

      const lCompChanged = l.completed !== b.completed;
      const rCompChanged = r.completed !== b.completed;
      const lTextChanged = l.text !== b.text;
      const rTextChanged = r.text !== b.text;

      const finalCompleted = rCompChanged && !lCompChanged ? r.completed : l.completed;
      const finalText = rTextChanged && !lTextChanged ? r.text : l.text;

      result.push({
        id,
        text: finalText,
        completed: finalCompleted
      });
    }
  });

  return result.length > 0 ? result : undefined;
}

/**
 * Merges task history entries without duplicates, sorted chronologically descending.
 */
export function mergeHistory(
  localHistory?: TaskHistoryEntry[],
  remoteHistory?: TaskHistoryEntry[]
): TaskHistoryEntry[] | undefined {
  if (!localHistory && !remoteHistory) return undefined;
  const loc = localHistory || [];
  const rem = remoteHistory || [];

  const historyMap = new Map<string, TaskHistoryEntry>();
  [...loc, ...rem].forEach(entry => {
    const key = entry.id || `${entry.timestamp}_${entry.field}_${entry.user}`;
    if (!historyMap.has(key)) {
      historyMap.set(key, entry);
    }
  });

  const merged = Array.from(historyMap.values()).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  return merged.slice(0, 100);
}

/**
 * Intelligent 3-way field-level merger for a single task.
 * Resolves non-conflicting field updates automatically via `mergeTaskFieldsWithBaseline`.
 * When local user is actively editing a conflicting field, local value is retained to prevent UX disruption.
 */
export function mergeTaskFields(local: Task, remote: Task, baseline?: Task): Task {
  return mergeTaskFieldsWithBaseline(baseline, local, remote);
}

/**
 * Merges incoming remote tasks into the local project task list.
 * - Non-dirty local tasks are updated with remote versions.
 * - Dirty local tasks are field-merged with remote changes.
 * - Newly added remote tasks are appended.
 * - Locally created (dirty) tasks are kept even if not yet on remote.
 * - Remotely deleted tasks are removed unless locally dirty.
 * - Recently deleted tasks (pending delete acknowledgment) are not resurrected.
 */
export function mergeProjectTasks(
  localTasks: Task[],
  remoteTasks: Task[],
  dirtyTaskIds: Set<string>,
  baselines?: Map<string, Task>,
  recentlyDeletedIds?: Set<string>
): Task[] {
  const localMap = new Map<string, Task>();
  localTasks.forEach(t => localMap.set(String(t.id), t));

  const remoteMap = new Map<string, Task>();
  remoteTasks.forEach(t => remoteMap.set(String(t.id), t));

  const mergedMap = new Map<string, Task>();

  // 1. Process all remote tasks
  remoteTasks.forEach(remoteTask => {
    const idStr = String(remoteTask.id);

    // If recently deleted locally, skip (do not resurrect)
    if (recentlyDeletedIds && recentlyDeletedIds.has(idStr)) {
      return;
    }

    const localTask = localMap.get(idStr);

    if (!localTask) {
      // If the task is marked dirty locally but absent from this project's localTasks,
      // it was moved out of this project locally and is still syncing — do not resurrect it here.
      if (dirtyTaskIds.has(idStr)) {
        return;
      }
      // Remote task not present locally -> add it!
      mergedMap.set(idStr, remoteTask);
    } else {
      const isDirty = dirtyTaskIds.has(idStr) || !!localTask.isInlineNew;
      if (!isDirty) {
        // Local has not modified this task -> adopt remote directly!
        mergedMap.set(idStr, remoteTask);
      } else {
        // Local is currently modifying this task -> perform 3-way field merge!
        const baseline = baselines?.get(idStr);
        const mergedTask = mergeTaskFields(localTask, remoteTask, baseline);
        mergedMap.set(idStr, mergedTask);
      }
    }
  });

  // 2. Process local tasks not in remote
  localTasks.forEach(localTask => {
    const idStr = String(localTask.id);
    if (!remoteMap.has(idStr)) {
      const isDirty = dirtyTaskIds.has(idStr) || !!localTask.isInlineNew;
      if (isDirty) {
        // Task was newly created locally and not yet synced to remote -> KEEP IT!
        mergedMap.set(idStr, localTask);
      } else {
        // Task was deleted remotely on Firestore and is not dirty locally -> DROP IT!
        // (Do not add to mergedMap)
      }
    }
  });

  // 3. Return sorted tasks
  const result = Array.from(mergedMap.values());
  return result.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

/**
 * Computes a diff object containing only the modified fields between current task and baseline.
 */
export function computeTaskDiff(current: Partial<Task>, baseline?: Partial<Task>): Partial<Task> {
  if (!baseline) return { ...current };

  const diffRecord: Record<string, unknown> = { id: current.id };
  const keys = Object.keys(current) as (keyof Task)[];

  keys.forEach(k => {
    if (k === 'id') return;
    if (isFieldDifferent(current[k], baseline[k])) {
      diffRecord[k as string] = current[k];
    }
  });

  return diffRecord as Partial<Task>;
}
